/**
 * Product compare — shared store, utilities, and data helpers.
 * Exposes window.ProductCompare (load before component files).
 */
(function () {
	'use strict';

	if (window.ProductCompare?.store) {
		return;
	}

	const STORAGE_KEY = 'nutriland_product_compare';
	const CHANGE_EVENT = 'dispatch:compare:change';
	const HANDLES_URL_PARAM = 'handles';

	/** @typedef {{ handle: string, title: string, url: string, image?: string, productType?: string }} CompareEntry */
	/** @typedef {{ handle: string, title: string, url: string, image: string, priceHtml: string, price: number, nutritionHtml: string, ingredientsHtml: string, productType?: string, costPerServingHtml?: string, costPerServing?: number | null, dailyCostPerServing?: number | null }} CompareProductPayload */

	/**
	 * @param {CompareEntry | CompareProductPayload | { productType?: string, type?: string }} item
	 * @returns {string}
	 */
	function normalizeCompareType(item) {
		const value = item?.productType ?? item?.type ?? '';

		return String(value).trim().toLowerCase();
	}

	function dispatchCompareChange() {
		document.dispatchEvent(
			new CustomEvent(CHANGE_EVENT, {
				detail: { handles: productCompareStore.getHandles(), entries: productCompareStore.getEntries() },
			}),
		);
	}

	/**
	 * @param {string} key
	 * @returns {string}
	 */
	function getCompareLocale(key) {
		const locales = window.KROWN?.settings?.locales || {};

		return locales[key] || '';
	}

	/**
	 * @param {'success' | 'error'} variant
	 * @param {string} localeKey
	 * @param {string} [fallback]
	 */
	function compareToast(variant, localeKey, fallback = '') {
		const host = document.querySelector('product-compare-toast-host');

		if (!host || typeof host.show !== 'function') {
			return;
		}

		const message = getCompareLocale(localeKey) || fallback;

		if (!message) {
			return;
		}

		host.show(message, variant);
	}

	/**
	 * @param {CompareEntry} entry
	 * @param {CompareEntry[]} existing
	 * @returns {'max' | 'missing_type' | 'type_mismatch' | null}
	 */
	function getCompareAddBlockReason(entry, existing) {
		const others = existing.filter((item) => item.handle !== entry.handle);

		if (others.length >= productCompareStore.maxProducts) {
			return 'max';
		}

		if (!others.length) {
			if (!normalizeCompareType(entry)) {
				return 'missing_type';
			}

			return null;
		}

		const newType = normalizeCompareType(entry);
		const baseType = normalizeCompareType(others[0]);

		if (!newType || !baseType) {
			return 'missing_type';
		}

		if (baseType !== newType) {
			return 'type_mismatch';
		}

		return null;
	}

	/**
	 * @param {CompareEntry} entry
	 * @param {CompareEntry[]} existing
	 */
	function canAddToCompare(entry, existing) {
		return getCompareAddBlockReason(entry, existing) === null;
	}

	/**
	 * @param {unknown} item
	 * @returns {CompareEntry | null}
	 */
	function normalizeStoredEntry(item) {
		if (!item || typeof item !== 'object' || !item.handle) {
			return null;
		}

		return {
			handle: String(item.handle),
			title: String(item.title || item.handle),
			url: String(item.url || `/products/${item.handle}`),
			image: String(item.image || ''),
			productType: String(item.productType || ''),
		};
	}

	/** @type {CompareEntry[] | null} */
	let memoryEntries = null;

	/** @returns {CompareEntry[]} */
	function readEntriesFromStorage() {
		try {
			const storedJson = localStorage.getItem(STORAGE_KEY);

			if (!storedJson) {
				return [];
			}

			const parsedEntries = JSON.parse(storedJson);

			if (!Array.isArray(parsedEntries)) {
				return [];
			}

			return parsedEntries.map(normalizeStoredEntry).filter(Boolean);
		} catch {
			return [];
		}
	}

	/** @returns {CompareEntry[]} */
	function getMemoryEntries() {
		if (memoryEntries === null) {
			memoryEntries = readEntriesFromStorage();
		}

		return memoryEntries;
	}

	/** @param {CompareEntry[]} entries */
	function writeMemoryEntries(entries) {
		memoryEntries = entries;
		localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
	}

	/**
	 * Prefer product types captured when products were added (tray), not inferred from collections.
	 * @param {string[]} handles
	 * @param {CompareProductPayload[]} payloads
	 */
	function validateCompareProductType(handles, payloads) {
		const entries = productCompareStore.getEntries().filter((entry) => handles.includes(entry.handle));

		if (entries.length >= 2 && entries.length === handles.length) {
			const storeTypes = entries.map((entry) => normalizeCompareType(entry));

			if (storeTypes.every((type) => type)) {
				return storeTypes.every((type) => type === storeTypes[0]);
			}
		}

		if (payloads.length < 2) {
			return true;
		}

		const payloadTypes = payloads.map((payload) => normalizeCompareType(payload));

		if (payloadTypes.some((type) => !type)) {
			return false;
		}

		return payloadTypes.every((type) => type === payloadTypes[0]);
	}

	/**
	 * @param {string | null | undefined} value
	 * @returns {string[]}
	 */
	function parseHandlesParam(value) {
		if (!value) {
			return [];
		}

		return value
			.split(',')
			.map((handle) => decodeURIComponent(handle.trim()))
			.filter(Boolean);
	}

	/** @returns {string[]} */
	function getHandlesFromUrl() {
		const params = new URLSearchParams(window.location.search);

		return parseHandlesParam(params.get(HANDLES_URL_PARAM) || params.get('products'));
	}

	/**
	 * @param {string[]} handles
	 */
	function syncHandlesToUrl(handles) {
		const url = new URL(window.location.href);
		const unique = [...new Set(handles.filter(Boolean))];

		if (unique.length >= 2) {
			url.searchParams.set(HANDLES_URL_PARAM, unique.join(','));
			url.searchParams.delete('products');
		} else {
			url.searchParams.delete(HANDLES_URL_PARAM);
			url.searchParams.delete('products');
		}

		window.history.replaceState({}, '', url);
	}

	/**
	 * @param {string} basePath
	 * @param {string[]} handles
	 */
	function buildComparePageUrl(basePath, handles) {
		const url = new URL(basePath || '/pages/compare', window.location.origin);
		const unique = [...new Set(handles.filter(Boolean))];

		if (unique.length) {
			url.searchParams.set(HANDLES_URL_PARAM, unique.join(','));
		}

		return url.toString();
	}

	/**
	 * @returns {string}
	 */
	function getComparePageBaseUrl() {
		const drawer = document.querySelector('product-compare-dock');
		const table = document.querySelector('product-compare-table');

		return drawer?.dataset.comparePageUrl || table?.dataset.comparePageUrl || '/pages/compare';
	}

	/**
	 * @param {string} firstPath
	 * @param {string} secondPath
	 */
	function isSamePath(firstPath, secondPath) {
		try {
			return new URL(firstPath, window.location.origin).pathname === new URL(secondPath, window.location.origin).pathname;
		} catch {
			return false;
		}
	}

	/**
	 * @param {CompareProductPayload[]} payloads
	 */
	function syncStoreFromPayloads(payloads) {
		const existingByHandle = Object.fromEntries(productCompareStore.getEntries().map((entry) => [entry.handle, entry]));

		const entries = payloads.map((payload) => {
			const existing = existingByHandle[payload.handle];

			return {
				handle: payload.handle,
				title: payload.title,
				url: payload.url,
				image: payload.image || '',
				productType: existing?.productType || payload.productType || '',
			};
		});

		writeMemoryEntries(entries.slice(0, productCompareStore.maxProducts || 3));
		dispatchCompareChange();
	}

	const productCompareStore = {
		/** @returns {CompareEntry[]} */
		getEntries() {
			return [...getMemoryEntries()];
		},

		/** @returns {string[]} */
		getHandles() {
			return this.getEntries().map((entry) => entry.handle);
		},

		/** @param {CompareEntry} entry */
		add(entry) {
			if (!canAddToCompare(entry, this.getEntries())) {
				return false;
			}

			const entries = this.getEntries().filter((item) => item.handle !== entry.handle);
			entries.unshift(entry);

			const max = productCompareStore.maxProducts || 3;
			const trimmed = entries.slice(0, max);

			writeMemoryEntries(trimmed);
			dispatchCompareChange();

			return true;
		},

		/** @param {string} handle */
		remove(handle) {
			const entries = this.getEntries().filter((item) => item.handle !== handle);

			writeMemoryEntries(entries);
			dispatchCompareChange();
		},

		clear() {
			memoryEntries = [];
			localStorage.removeItem(STORAGE_KEY);
			dispatchCompareChange();
		},

		/** @param {string} handle */
		has(handle) {
			return this.getHandles().includes(handle);
		},

		maxProducts: 3,
	};

	/**
	 * Normalize label key for row union.
	 * @param {string} label
	 */
	function normalizeKey(label) {
		return (label || '')
			.replace(/&nbsp;/g, ' ')
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.replace(/^[\s-]+/, '')
			.replace(/\s+/g, ' ')
			.trim();
	}

	/**
	 * Convert rich text / HTML to plain lines.
	 * @param {string} html
	 */
	function htmlToLines(html) {
		if (!html) {
			return [];
		}

		const isLine = (line) => line && line.replace(/\s/g, '').length > 0;

		return html
			.replace(/<\/p>/gi, '\n')
			.replace(/<br\s*\/?>/gi, '\n')
			.replace(/<\/div>/gi, '\n')
			.replace(/<p[^>]*>/gi, '')
			.split('\n')
			.map((line) =>
				line
					.replace(/<[^>]+>/g, '')
					.replace(/\r/g, '')
					.trim(),
			)
			.filter(isLine);
	}

	/**
	 * Parse nutrition lines into ordered rows.
	 * @param {string} html
	 */
	function parseNutritionRows(html) {
		const rows = [];
		const valuesByLabelKey = {};

		for (const rawLine of htmlToLines(html)) {
			const isIndented = /^\s*-/.test(rawLine);
			const line = isIndented ? rawLine.replace(/^\s*-/, '').trim() : rawLine.trim();
			const match = line.split(/,(.+)/);
			const label = (match[0] || line).trim();
			const value = match.length >= 2 ? match[1].trim() : '';
			const key = normalizeKey(label);

			if (!key) {
				continue;
			}

			const row = {
				key,
				label,
				value: value || '—',
				isIndented,
			};

			rows.push(row);
			valuesByLabelKey[key] = row.value;
		}

		return { rows, valuesByLabelKey };
	}

	/**
	 * Strip HTML to plain text for ingredients block.
	 * @param {string} html
	 */
	function htmlToPlainText(html) {
		if (!html) {
			return '';
		}

		return html
			.replace(/<\/p>/gi, '\n')
			.replace(/<br\s*\/?>/gi, '\n')
			.replace(/<[^>]+>/g, '')
			.replace(/\s+\n/g, '\n')
			.trim();
	}

	/**
	 * Fetch product compare payload via section rendering.
	 * @param {string} handle
	 * @param {string} helperSectionId
	 * @param {{ signal?: AbortSignal }} [options]
	 */
	async function fetchProductPayload(handle, helperSectionId, options = {}) {
		const url = `/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(helperSectionId)}`;
		const response = await fetch(url, { signal: options.signal });

		if (!response.ok) {
			throw new Error(`Failed to load product ${handle} (${response.status})`);
		}

		const sectionHtml = await response.text();
		const parsedSectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
		let script = parsedSectionDocument.querySelector('[data-product-compare-payload]');
		let jsonText = script?.textContent?.trim() || '';

		if (!jsonText) {
			const match = sectionHtml.match(/<script[^>]*data-product-compare-payload[^>]*>([\s\S]*?)<\/script>/i);

			if (match) {
				jsonText = match[1].trim();
			}
		}

		if (!jsonText) {
			throw new Error(`Missing compare payload for ${handle}`);
		}

		try {
			return /** @type {CompareProductPayload} */ (JSON.parse(jsonText));
		} catch (error) {
			console.error(`Invalid compare JSON for ${handle}:`, jsonText.slice(0, 200), error);
			throw new Error(`Invalid compare payload for ${handle}`);
		}
	}

	/**
	 * Build union row order from multiple nutrition row sets.
	 * @param {Array<{ rows: Array<{ key: string, label: string, isIndented: boolean }> }>} parsedList
	 */
	function buildUnionRows(parsedList) {
		const union = [];
		const seen = new Set();

		for (const nutritionParseResult of parsedList) {
			for (const row of nutritionParseResult.rows) {
				if (seen.has(row.key)) {
					continue;
				}

				seen.add(row.key);
				union.push({
					key: row.key,
					label: row.label,
					isIndented: row.isIndented,
				});
			}
		}

		return union;
	}

	function syncCompareToggleButtons() {
		const entries = productCompareStore.getEntries();
		const atMax = entries.length >= productCompareStore.maxProducts;

		document.querySelectorAll('[data-product-compare-toggle]').forEach((button) => {
			const handle = button.dataset.productHandle;
			const isActive = productCompareStore.has(handle);
			const isDisabled = atMax && !isActive;
			const title = button.dataset.productTitle || handle || '';
			const addLabelTemplate = button.dataset.addLabel || '';
			const removeLabelTemplate = button.dataset.removeLabel || '';
			const labelTemplate = isActive ? removeLabelTemplate : addLabelTemplate;
			const ariaLabel = labelTemplate.replace(/\{\{\s*title\s*\}\}/g, title);

			button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

			if (ariaLabel) {
				button.setAttribute('aria-label', ariaLabel);
			}

			button.classList.toggle('product-compare-button--active', isActive);
			button.classList.toggle('product-compare-button--disabled', isDisabled);
			button.disabled = isDisabled;
		});
	}

	function onCompareToggleClick(event) {
		const button = event.target.closest('[data-product-compare-toggle]');

		if (!button) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		const handle = button.dataset.productHandle;

		if (!handle) {
			return;
		}

		const entry = {
			handle,
			title: button.dataset.productTitle || handle,
			url: button.dataset.productUrl || `/products/${handle}`,
			image: button.dataset.productImage || '',
			productType: button.dataset.productType || '',
		};

		if (productCompareStore.has(handle)) {
			productCompareStore.remove(handle);
			compareToast('success', 'compare_toast_removed', 'Product removed from comparison list.');

			return;
		}

		const existing = productCompareStore.getEntries();
		const blockReason = getCompareAddBlockReason(entry, existing);

		if (blockReason === 'max') {
			compareToast('error', 'compare_max_reached', 'Maximum products in compare list reached.');

			return;
		}

		if (blockReason === 'missing_type') {
			compareToast('error', 'compare_toast_missing_type', 'This product has no type set and cannot be compared.');

			return;
		}

		if (blockReason === 'type_mismatch') {
			compareToast('error', 'compare_toast_same_type', 'Products must share the same product type to compare.');

			return;
		}

		productCompareStore.add(entry);
		compareToast('success', 'compare_toast_added', 'Product added to comparison list.');
	}

	/**
	 * @param {string} value
	 */
	function escapeHtmlAttr(value) {
		return String(value || '')
			.replace(/&/g, '&amp;')
			.replace(/"/g, '&quot;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;');
	}

	/** @param {string} value */
	function escapeHtml(value) {
		return escapeHtmlAttr(value);
	}

	/**
	 * @param {string} [url]
	 * @param {string} [handle]
	 */
	function escapeCompareUrl(url, handle) {
		const fallback = `/products/${encodeURIComponent(handle || '')}`;
		const candidateUrl = String(url || fallback).trim();

		if (!candidateUrl || /^javascript:/i.test(candidateUrl) || /^data:/i.test(candidateUrl)) {
			return escapeHtmlAttr(fallback);
		}

		return escapeHtmlAttr(candidateUrl);
	}

	window.ProductCompare = {
		CHANGE_EVENT,
		HANDLES_URL_PARAM,
		store: productCompareStore,
		normalizeCompareType,
		dispatchCompareChange,
		getCompareLocale,
		compareToast,
		getCompareAddBlockReason,
		canAddToCompare,
		validateCompareProductType,
		parseHandlesParam,
		getHandlesFromUrl,
		syncHandlesToUrl,
		buildComparePageUrl,
		getComparePageBaseUrl,
		isSamePath,
		syncStoreFromPayloads,
		normalizeKey,
		htmlToLines,
		parseNutritionRows,
		htmlToPlainText,
		fetchProductPayload,
		buildUnionRows,
		syncCompareToggleButtons,
		onCompareToggleClick,
		escapeHtmlAttr,
		escapeHtml,
		escapeCompareUrl,
	};
})();
